/**
 * FieldRecordsList (Inventory Screens Step 5.1) — the mobile READ-ONLY
 * capture list. Columns follow the active INV/ALL view (columnsForView
 * below; Bob P1 item 2a), one row per record keyed on record_id
 * (NEVER tool_id — TOOL_IDs are not unique client-side; duplicates must
 * render as distinct rows), Load order, no sorting.
 *
 * Layout: two-row sticky header. Row 1 = context bar (back → sites view,
 * site name + count) — a fixed flex child. Row 2 = column heads — sticky
 * <thead> inside the single overflow:auto scroller, so it pins vertically
 * and scrolls horizontally WITH the body.
 *
 * Unit 4 Phase 1 adds a third band: FieldRecordsFooter, a flex-none sibling
 * BELOW the scroller. .frl was already a 100dvh flex column with .frl-scroll
 * at flex:1, so the bar bottoms out on its own and the scroller stops above
 * it — no position:fixed, and no bottom padding to keep in sync with the
 * bar's height. Add Tool moved OUT of the context bar into that footer; the
 * empty-state "Add the first tool" link is untouched and remains the
 * affordance a rep meets on a site with no records yet.
 *
 * Read-only grid. 5.2 Phase 1: rows become tappable when the host passes
 * onRowTap(record) — the host owns what a tap opens (the record modal, via
 * router-native state navigation). Without the prop, rows stay inert.
 * Still no editing, no producers, no sort/group/summaries.
 *
 * Unit 4 Phase 2 adds HIDE: the host passes a `hidden` Set of field keys and
 * the view's columns are filtered by it. The filtered list is derived ONCE and
 * feeds the header cells, the body cells and the empty row's colSpan — three
 * consumers that must never disagree about the column count, which is exactly
 * what a second inline filter would eventually let happen.
 *
 * The grid can never be emptied by hiding. MEDIA carries key:null and is not
 * hideable at all (no manifest lists it), and supplier_tool_id is refused by
 * useFieldHiddenFields on every path — so at minimum TOOL ID and MEDIA
 * survive any combination of toggles, including None.
 */

import React from 'react';
import FieldRecordsFooter from './FieldRecordsFooter';
import { splitPickerFields } from './FieldPickerOptions';
import {
  MEDIA_AFTER_KEY, VISIBLE_CHILD_SLOTS, fieldsForView, withChildColumns,
} from '../data/editableFields';
import { formatReadonly } from '../data/formatReadonly';
import { recordLabel } from '../recordLabel';
import './FieldRecordsList.css';

/* ── The grid's columns: an ADAPTER over fieldsForView (Bob P1 item 2a) ──
   The grid used to own a hand-written INV-only column list and ignored the
   view. It now reads the SAME view→field-set mapping the Columns picker
   reads, and only translates it. Three things the manifest cannot say:

   1. KIND. The manifest speaks text | number | bool | enum (read-only rows
      carry none); a cell only needs flag (bools render as a chip) or text.
   2. MEDIA. A computed column (record.media.length) with no field behind
      it, so no manifest lists it. It rides directly after ADDED, where the
      grid has always shown it.
   3. TOOL ID FIRST. The picker's pin (splitPickerFields, PINNED_FIRST_KEYS)
      is reused rather than restated, so the grid and the Sort/Filter/Group
      pickers cannot disagree about what leads.

   In INV the grid shows exactly what INV_KEYS and READONLY_INV_KEYS admit,
   plus MEDIA. It began as the old hand list's set plus OEM; whatever either
   Set gains since reaches the grid with no change here.

   4. CHILD VALUES (P1-2 piece 1). ALL also gets PART NUMBER 1-3 and ASSET
      TAG 1-3 (kind 'childValue'), spliced in by withChildColumns — the same
      call the Columns picker makes, so the two list them in one order. Like
      MEDIA they have no field behind them; unlike MEDIA they have real keys
      (pn_1 … at_3), so they hide like any other column. */
const MEDIA_COLUMN = { label: 'MEDIA', key: null, kind: 'mediaCount' };
const NOTHING_HIDDEN = new Set();

/* B-254 (P2-2 part A): the three flag columns that render as checkboxes. ONE definition, read
   by both the <td> class and cellContent. The other four bool columns (CAPACITY, NPN, APPROVED
   SCRAP, MARKED) stay on the 'flag' arm, words included: MARKED holds 'LOCATE' strings on real
   records, which a checkbox would show unchecked and one tap would overwrite (spec D-1). Their
   column kind stays 'flag' too, so nothing that reads kinds moves. */
const TAP_FLAG_KEYS = new Set(['fix', 'found', 'added_by_tmg']);

/* B-254 Phase 2 — a tap on a flag cell toggles its box and never opens the record. ONE
   stopPropagation on the label's click covers both clicks a tap can produce: the label's own,
   and the click the browser then sends to the box, which bubbles back through this label.
   Neither reaches the row's onRowTap.
   Nothing is cancelled. The toggle arrives as the box's onChange, and the box stays controlled:
   React holds it at the record's value until the host's update re-renders it, so it moves only
   once the write has landed (D-11). preventDefault on a click that started on a controlled box
   fights React's restore and leaves the box flipped (Gate 1a, measured). */
function stopAtFlag(e) {
  e.stopPropagation();
}

function toGridColumn(field) {
  const column = {
    label: field.label,
    key: field.key,
    kind: field.kind === 'bool' ? 'flag' : 'text',
  };
  // A manifest display format rides along for cellContent (Phase 2).
  if (field.format) column.format = field.format;
  return column;
}

/** Every column the view offers, in grid order, before the hide filter. */
export function columnsForView(view) {
  const { visible } = splitPickerFields(fieldsForView(view), NOTHING_HIDDEN);
  const columns = visible.map(toGridColumn);
  // A missing anchor appends rather than drops: MEDIA must never vanish.
  const anchor = columns.findIndex((col) => col.key === MEDIA_AFTER_KEY);
  columns.splice(anchor === -1 ? columns.length : anchor + 1, 0, MEDIA_COLUMN);
  return withChildColumns(columns, view);
}

/** One child array off the stored row; anything but an array reads as []. */
const childArray = (record, key) => (Array.isArray(record?.[key]) ? record[key] : []);

/** An entry's display text. Nullish → '', anything else → String(value). */
const childText = (entry) => String(entry?.value ?? '');

/**
 * Ascending on the display text; blank (empty after trim) and nullish values
 * LAST, so they take a visible slot only when nothing else is left to fill it.
 * Plain < / >, never localeCompare: two devices must order identically, and
 * localeCompare follows each device's locale. Ties keep their stored order —
 * Array#sort is stable.
 */
function byValue(a, b) {
  const av = childText(a);
  const bv = childText(b);
  const aBlank = av.trim() === '';
  const bBlank = bv.trim() === '';
  if (aBlank !== bBlank) return aBlank ? 1 : -1;
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
}

// EXPORTED for the Unit 6 search overlay, which renders the same row shape
// against the same records. Logic UNCHANGED — one renderer means a matched
// cell in the overlay cannot show something different from the same cell on
// the records screen, which is the whole reason a match is trustworthy.
export function cellContent(col, record, onFlagTap) {
  if (col.kind === 'mediaCount') {
    return <span className="frl-media-count">{record.media?.length ?? 0}</span>;
  }
  // Child values live BESIDE `working` (fieldStore.js), never in it. Load puts
  // slots 1-10 in the primary array and 11+ in extra_*; a local edit flattens
  // both into the primary — so the two are always read together.
  if (col.kind === 'childValue') {
    const all = [...childArray(record, col.group), ...childArray(record, col.extraKey)].sort(byValue);
    const entry = all[col.slot];
    if (entry === undefined) return '';
    const extra = all.length - VISIBLE_CHILD_SLOTS;
    // The marker is display only: no handler, no button, no link.
    return (
      <span className="frl-child">
        <span className="frl-child-value">{childText(entry)}</span>
        {col.last && extra > 0 && <span className="frl-child-more">+{extra}</span>}
      </span>
    );
  }
  const value = record.working ? record.working[col.key] : undefined;
  if (TAP_FLAG_KEYS.has(col.key)) {
    // D-4: only true is checked; blank and false both read unchecked, as in the record view.
    // No id: nesting associates the label, and an id would repeat on every row and collide
    // with the modal's frm-bool-* ids. The column header is in <thead>, so the name carries
    // the row too, or every FOUND box would announce identically. Without an onFlagTap the box
    // is read-only by intent (readOnly: React warns on checked without onChange); the stop
    // still applies, so a flag cell is never a way into the record.
    return (
      <label className="frl-flag" onClick={stopAtFlag}>
        <input
          type="checkbox"
          className={`frl-flag-box frl-flag-box-${col.key}`}
          checked={value === true}
          onChange={onFlagTap ? () => onFlagTap(record, col.key) : undefined}
          readOnly={!onFlagTap}
          aria-readonly={onFlagTap ? undefined : 'true'}
          aria-label={`${col.label}, ${recordLabel(record)}`}
        />
      </label>
    );
  }
  if (value === null || value === undefined || value === '') return '';
  if (col.kind === 'flag') {
    return <span className="frl-chip">{String(value)}</span>;
  }
  // The record form's formatter, not a second one (Bob P1 item 2a): a column
  // whose manifest entry carries `format` reads exactly as the form shows it,
  // and one without falls through to String(value) as before.
  return formatReadonly(col, value);
}

// F-43: stable empty default for dirtyIds. A `new Set()` written as a
// default parameter would mint a fresh identity on every render; this is
// module scope and never mutated. A missing prop therefore renders no
// dirty rows rather than throwing on .has().
const EMPTY_DIRTY_IDS = new Set();

// Same reasoning, for FieldRecordRow's `matched` prop below.
const EMPTY_MATCHED = new Set();

/**
 * FieldRecordRow (Unit 6 Phase 2) — ONE record row, extracted verbatim from
 * this file's own tbody map so the search overlay can render results that are
 * the same row, not a lookalike. Two surfaces, one definition: a column that
 * ellipsises here ellipsises there, a dirty tint reads the same, and neither
 * can drift into showing a different value for the same cell.
 *
 * The `<tr>`/`<td>` shape is load-bearing and travels with it — a caller has
 * to supply a <table><tbody> ancestor, which is why the overlay wraps its
 * results in .frl-table rather than inventing a div grid.
 *
 * `matched` is the only addition, and it is inert by default: with the empty
 * default Set the emitted className is byte-identical to the pre-extraction
 * string, so the records screen renders exactly as it did before.
 *
 * `onFlagTap(record, key)` (B-254 Phase 2) is what a tap on FIX, FOUND or ADDED calls. The
 * grid and the overlay both pass the host's; without it the boxes are read-only.
 */
export function FieldRecordRow({
  record, visibleColumns, dirtyIds = EMPTY_DIRTY_IDS, onRowTap, matched = EMPTY_MATCHED, onFlagTap,
}) {
  return (
    <tr
      data-testid="field-record-row"
      /* frl-row-dirty is COMPOSED with frl-row-tappable, never
         substituted for it — a row holding unsent edits is
         exactly the row a rep must still be able to tap.
         F-43: the flag now comes from the host's LIVE dirtyIds
         Set, not from this row's own `dirty` object. The rows
         arrive in a one-shot snapshot, so a reconcile that
         cleared dirty never reached them and the tint could not
         clear. Reading an id Set also keeps this component
         presentation-only — no predicate, no fieldStore import,
         no Dexie in a dumb grid, and no second definition of
         'dirty' left to drift. */
      className={[
        onRowTap ? 'frl-row-tappable' : null,
        dirtyIds.has(record.record_id) ? 'frl-row-dirty' : null,
      ].filter(Boolean).join(' ') || undefined}
      onClick={onRowTap ? () => onRowTap(record) : undefined}
    >
      {/* key is col.label, NOT col.key — MEDIA's key is null and every row
          would collide on it. */}
      {visibleColumns.map((col) => (
        <td
          key={col.label}
          className={[
            col.kind === 'text' ? 'frl-cell-text'
              : col.kind === 'childValue' ? 'frl-cell-child'
                : 'frl-cell-badge',
            TAP_FLAG_KEYS.has(col.key) ? 'frl-cell-flag' : null,
            col.key && matched.has(col.key) ? 'fso-cell-hit' : null,
          ].filter(Boolean).join(' ')}
        >
          {cellContent(col, record, onFlagTap)}
        </td>
      ))}
    </tr>
  );
}

/**
 * The view's columns minus the hidden ones, exported so the host (search
 * overlay) and the grid share ONE definition. `view` defaults to INV, the
 * zone's default view.
 */
export function visibleColumnsFor(hidden, view = 'inv') {
  return columnsForView(view).filter(
    (col) => col.key === null || !hidden.has(col.key),
  );
}

// Same reasoning as EMPTY_DIRTY_IDS above, for the same reason: a missing
// `hidden` prop must render every column, not throw on .has().
const EMPTY_HIDDEN = new Set();

/**
 * The "nothing is collapsed" value, EXPORTED so the host's useState initialiser
 * and this component's prop default are the SAME reference.
 *
 * One definition rather than two, for visibleColumnsFor's reason. The host owns
 * the collapse Set and this file owns the keys inside it; two module-scope empty
 * Sets that both mean "nothing collapsed" are two references that compare
 * UNEQUAL, and that identity is what makes the host's site-change reset free —
 * a setState to the value React already holds is a bail-out, not a render.
 *
 * Object.freeze states the intent but does NOT seal a Set: freeze guards
 * properties, and .add() writes an internal slot. The real guarantee is the
 * convention EMPTY_DIRTY_IDS and EMPTY_HIDDEN already rely on — module scope,
 * never mutated — plus a toggle that builds a NEW Set on every change.
 */
/* Shared with FieldZonePlaceholder deliberately. The prop default
   here, the host's initial state and its site-change reset must be
   the SAME reference or none of the three is a React bail-out. Do
   not replace with a file-local copy. */
export const EMPTY_COLLAPSED = Object.freeze(new Set());

/**
 * A level-2 collapse key: the two bucket values joined by a NUL.
 *
 * KEY CONSTRUCTION LIVES HERE, not in the host. The host stores opaque strings
 * and never parses one, so the format is this file's business alone.
 *
 * A NUL (\u0000) rather than any printable delimiter, because bucket values ARE
 * arbitrary user text: a tool code carries slashes, a location carries dashes,
 * an inventory comment carries whatever a phone keyboard can produce. Any
 * printable separator can therefore be typed INTO a value and collide two
 * different (parent, child) pairs onto one key — at which point collapsing one
 * bucket silently closes another somewhere else in the grid. NUL is the one
 * character a rep cannot enter.
 */
function childCollapseKey(parentValue, childValue) {
  return `${parentValue}\u0000${childValue}`;
}

/**
 * FieldGroupHeaderRow (Unit 5 Phase 3c) — one bucket's disclosure band.
 *
 * ONE definition used at BOTH levels, differing only by `level`. Writing the
 * level-1 and level-2 headers out separately would be two copies of the same
 * <tr>/<td>/<button>/chevron chrome, and this file has twice already recorded
 * what a second copy of a rendering rule costs (visibleColumnsFor's three
 * consumers, FieldRecordRow's two surfaces). This is NOT a recursive renderer:
 * the caller runs two explicit loops, because groupRecords hard-caps depth at 2
 * and writes children:null on every child it emits.
 *
 * The <td> spans every visible column, so the band reads across the grid rather
 * than sitting inside the TOOL ID column. colSpan comes from the caller's
 * already-derived visibleColumns.length — the same single source the thead, the
 * body cells and both empty rows read, which is what keeps four consumers from
 * ever disagreeing about the column count.
 *
 * `count` renders EXACTLY as groupRecords supplied it. That number is the ROW
 * total beneath the bucket, never the number of child buckets, and recomputing
 * it here from rows/children is how "DIE — 34" eventually comes to mean four of
 * something the site holds thirty-four of.
 *
 * NOT STICKY. The thead is already position:sticky at z-index 2 inside the one
 * .frl-scroll; a second sticky band in the same scroller would have to
 * out-stack it, pin beneath it and reserve its own height — three new problems
 * for a band the rep flicks past in one swipe.
 */
function FieldGroupHeaderRow({ label, count, colSpan, collapsed, level, onToggle }) {
  return (
    /* NO class on the <tr>. Nothing styles the row itself, and
       FieldRecordsList.css has already recorded, in its note on
       .frl-cell-badge, what a class the JSX emits and the stylesheet never
       defines costs: a dangling reference nobody can tell from a rule that
       was meant to exist. The level distinction rides on the CELL, which is
       where the padding and the background it changes actually live. */
    <tr data-testid="field-group-header">
      <td
        className={level === 2 ? 'frl-group-cell frl-group-cell-sub' : 'frl-group-cell'}
        colSpan={colSpan}
      >
        <button
          type="button"
          className="frl-group-toggle"
          data-testid="field-group-toggle"
          aria-expanded={!collapsed}
          onClick={onToggle}
        >
          <svg
            className={`frl-group-chevron${collapsed ? '' : ' frl-group-chevron-open'}`}
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M6 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="frl-group-label">{label}</span>
          <span className="frl-group-count">{count}</span>
        </button>
      </td>
    </tr>
  );
}

export default function FieldRecordsList({
  site, records, dirtyIds = EMPTY_DIRTY_IDS, onBack, onRowTap, onFlagTap, onAddTool, onColumns,
  onSearch, onView, hidden = EMPTY_HIDDEN, fieldView = 'inv', onFieldViewChange,
  totalRecords, filterActive = false, onClearFilter, pruneDryRun = null, staleNotice = null,
  groups = null, collapsedGroups = EMPTY_COLLAPSED, onToggleGroup,
}) {
  // Unit 5 Phase 2 — "N of M records" needs the UNFILTERED total, and the host
  // passes it down rather than this component recomputing it. It cannot be
  // recomputed here: `records` IS the filtered array by the time it arrives,
  // so the information is simply not in this component. Falling back to
  // records.length keeps the count honest for every caller that does not
  // filter (and makes "N of N" degrade to plain "N", below).
  const total = typeof totalRecords === 'number' ? totalRecords : records.length;
  const showOfTotal = filterActive && total !== records.length;
  // Derived ONCE — header, body and the empty row's colSpan all read this.
  // MEDIA (key:null) is exempt by construction: it is a computed column with
  // no field behind it, so it appears in no manifest, cannot be listed in the
  // Columns modal, and must not be filtered out by a hidden set it can never
  // be a member of. Testing `col.key === null` first also keeps a null from
  // ever reaching Set.has().
  const visibleColumns = visibleColumnsFor(hidden, fieldView);

  // ── Unit 5 Phase 3c: the grouped path ───────────────────────────────────
  // TWO explicit loops, never a recursive walk. groupRecords hard-caps depth at
  // 2 and writes children:null on every child, so a general tree walker would
  // carry a recursion it can never take and every reader would have to prove
  // that for themselves before trusting it.
  //
  // ORDER AND (blank) ARE ALREADY RIGHT. Buckets arrive collated by the shared
  // collator with the blank bucket pinned last, at BOTH levels. This renders
  // them in the order given and treats '(blank)' as the string it is —
  // re-sorting here, or special-casing that label, would be a second opinion
  // about bucket order for the grid to disagree with the Sort sheet over.
  //
  // AN EMPTY TREE RENDERS NOTHING and falls through to the empty-state rows
  // below. That is exact rather than lucky: groupRecords buckets every row it is
  // handed, so groups.length === 0 happens only when records.length === 0 —
  // precisely what those two rows test. No empty grouped shell.
  const renderGrouped = () => groups.map((bucket) => {
    const collapsed = collapsedGroups.has(bucket.value);
    return (
      <React.Fragment key={bucket.value}>
        <FieldGroupHeaderRow
          label={bucket.value}
          count={bucket.count}
          colSpan={visibleColumns.length}
          collapsed={collapsed}
          level={1}
          onToggle={() => onToggleGroup(bucket.value)}
        />
        {/* Collapsed hides EVERYTHING beneath it, child headers included —
            there is no state in which a rep sees level-2 chrome under a bucket
            they have closed. `rows` and `children` are mutually exclusive by
            groupRecords' construction, so these two branches can never both
            run and neither has to decide which of two populated fields wins. */}
        {!collapsed && bucket.rows && bucket.rows.map((record) => (
          <FieldRecordRow
            key={record.record_id}
            record={record}
            visibleColumns={visibleColumns}
            dirtyIds={dirtyIds}
            onRowTap={onRowTap}
            onFlagTap={onFlagTap}
          />
        ))}
        {!collapsed && bucket.children && bucket.children.map((child) => {
          const childKey = childCollapseKey(bucket.value, child.value);
          const childClosed = collapsedGroups.has(childKey);
          return (
            <React.Fragment key={childKey}>
              <FieldGroupHeaderRow
                label={child.value}
                count={child.count}
                colSpan={visibleColumns.length}
                collapsed={childClosed}
                level={2}
                onToggle={() => onToggleGroup(childKey)}
              />
              {!childClosed && child.rows.map((record) => (
                <FieldRecordRow
                  key={record.record_id}
                  record={record}
                  visibleColumns={visibleColumns}
                  dirtyIds={dirtyIds}
                  onRowTap={onRowTap}
                  onFlagTap={onFlagTap}
                />
              ))}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  });

  return (
    <div className="frl" data-testid="field-records-view">
      {/* Header row 1 — context */}
      <div className="frl-context">
        <button type="button" className="frl-back" onClick={onBack} aria-label="Back to sites">
          ‹ Sites
        </button>
        <div className="frl-context-info">
          <div className="frl-site-name">{site?.site_name || site?.site_id}</div>
          <div className="frl-record-count" data-testid="field-records-count">
            {/* "12 of 841 records" while a filter is on, plain "841 records"
                otherwise. The denominator is the point: without it a rep
                cannot tell a narrow filter from a small site, and the whole
                reason this line changed is that a filtered grid and an empty
                one look identical from arm's length. */}
            {showOfTotal
              ? `${records.length} of ${total} record${total === 1 ? '' : 's'}`
              : `${records.length} record${records.length === 1 ? '' : 's'}`}
          </div>
          {/* Ghost prune Phase A1 instrument: what the latest online Load WOULD
              have pruned. Its own line, so the record count above keeps its
              exact text. Nothing is deleted. The host passes it only on a device
              that opted in (A1.5: five taps on the Build stamp), so a rep never
              sees it. */}
          {pruneDryRun && (
            <div className="frl-record-count" data-testid="field-prune-dryrun">
              {pruneDryRun.status === 'complete'
                ? `Prune dry run: ${pruneDryRun.count} of ${pruneDryRun.storedCount}`
                : `Prune dry run: skipped (${pruneDryRun.status === 'skipped-incomplete-walk'
                  ? 'incomplete load' : 'queue unreadable'})`}
            </div>
          )}
        </div>
        {/* INV/ALL switch (Bob P1 item 2a, fault 2): "INV and ALL switch from
            the grid header, not from inside a record or the New Tool form."
            Renders only when the host supplies a setter — no handler, no
            control, the contract the record form's copy had. This header is
            field-zone-owned, so the ModalShell no-header-slot limit that put
            the switch inside the form never applied here. */}
        {onFieldViewChange && (
          <div className="frl-view-switch" role="group" aria-label="Field view">
            <button
              type="button"
              className={`frl-view-btn${fieldView !== 'all' ? ' frl-view-btn-on' : ''}`}
              aria-pressed={fieldView !== 'all'}
              onClick={() => onFieldViewChange('inv')}
              data-testid="frl-view-inv"
            >
              INV
            </button>
            <button
              type="button"
              className={`frl-view-btn${fieldView === 'all' ? ' frl-view-btn-on' : ''}`}
              aria-pressed={fieldView === 'all'}
              onClick={() => onFieldViewChange('all')}
              data-testid="frl-view-all"
            >
              ALL
            </button>
          </div>
        )}
      </div>

      {/* B-224: the rows below are the saved copy, served after this site's
          Load failed. The host owns the state and passes the text, or null;
          this column owns the layout, so the band sits under row 1 (the back
          control stays above it) and above the rows. Not dismissible. */}
      {staleNotice && (
        <div className="frl-stale-banner" role="status" data-testid="field-records-stale">
          {staleNotice}
        </div>
      )}

      {/* Header row 2 (sticky thead) + body — one scroller, both axes */}
      <div className="frl-scroll">
        <table className="frl-table">
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th key={col.label}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups ? renderGrouped() : records.map((record) => (
              <FieldRecordRow
                key={record.record_id}
                record={record}
                visibleColumns={visibleColumns}
                dirtyIds={dirtyIds}
                onRowTap={onRowTap}
                onFlagTap={onFlagTap}
              />
            ))}
            {/* TWO empty states, and telling them apart is the whole point.
                A rep who filters to zero rows and meets "No records on device
                for this site" will conclude the site failed to load and walk
                away — the copy has to name the filter as the cause and hand
                them the way out. The unfiltered copy is UNCHANGED, including
                its "Add the first tool" affordance, which stays the thing a
                rep meets on a genuinely empty site. */}
            {records.length === 0 && filterActive && (
              <tr>
                <td className="frl-empty" colSpan={visibleColumns.length}>
                  {total === 1
                    ? 'No records match the filter. 1 record on this site.'
                    : `No records match the filter. ${total} records on this site.`}
                  {onClearFilter && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="frl-empty-add"
                        data-testid="field-clear-filter-empty"
                        onClick={onClearFilter}
                      >
                        Clear filters
                      </button>
                    </>
                  )}
                </td>
              </tr>
            )}
            {records.length === 0 && !filterActive && (
              <tr>
                <td className="frl-empty" colSpan={visibleColumns.length}>
                  No records on device for this site.
                  {onAddTool && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="frl-empty-add"
                        data-testid="field-add-tool-empty"
                        onClick={onAddTool}
                      >
                        Add the first tool
                      </button>
                    </>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Action bar — OUTSIDE .frl-scroll, so it never overlays a row and
          the scroller never has to reserve space for it. Unit 6 P1 supplied
          onSearch and Unit 5 P1 supplies onView, so the footer now builds all
          four slots and three dividers; this component is a pass-through in
          both cases — the footer's own `if (onView)` guard is the whole
          mechanism, exactly as `if (onSearch)` was. */}
      <FieldRecordsFooter
        onColumns={onColumns}
        onAdd={onAddTool}
        onSearch={onSearch}
        onView={onView}
      />
    </div>
  );
}
